import type { RefObject } from "react";
import { Loader2, Send, Square, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LiveWaveform } from "./LiveWaveform";

interface DictationRecorderProps {
  peaksRef: RefObject<number[]>;
  durationMs: number;
  isTranscribing: boolean;
  /** Model hazır ve yükleme yok; transkript doğrudan gönderilebilir. */
  canSend: boolean;
  onCancel: () => void;
  /** Kaydı bitir, transkripti kutuya yaz. */
  onConfirm: () => void;
  /** Kaydı bitir, transkripti olduğu gibi gönder. */
  onConfirmAndSend: () => void;
}

const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Kayıt şeridi composer satırının yerini alır.
 * Sıra: iptal, dalga, süre, durdur (metin kutuya gelir), gönder (doğrudan gider).
 * Mantıksal sıra; Arapça satırın geri kalanıyla birlikte aynalanır.
 */
export const DictationRecorder = ({
  peaksRef,
  durationMs,
  isTranscribing,
  canSend,
  onCancel,
  onConfirm,
  onConfirmAndSend,
}: DictationRecorderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full min-w-0 items-center gap-1.5 py-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 flex-shrink-0 rounded-full border border-border text-muted-foreground hover:bg-secondary hover:text-foreground dark:hover:bg-gray-700"
        onClick={onCancel}
        disabled={isTranscribing}
        aria-label={t("chat.dictateCancel")}
        title={t("chat.dictateCancel")}
      >
        <X className="h-5 w-5" />
      </Button>

      <div
        className={cn(
          "relative h-9 min-w-0 flex-1",
          isTranscribing && "opacity-40"
        )}
      >
        <LiveWaveform peaksRef={peaksRef} />
        {isTranscribing && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/40 backdrop-blur-[1px] dark:bg-gray-900/40">
            <Loader2 className="h-5 w-5 animate-spin text-text-secondary dark:text-gray-300" />
            <span className="truncate text-sm text-text-secondary dark:text-gray-300">
              {t("chat.dictateTranscribing")}
            </span>
          </div>
        )}
      </div>

      <span className="w-10 flex-shrink-0 text-sm font-medium tabular-nums text-text-secondary dark:text-gray-300">
        {formatDuration(durationMs)}
      </span>

      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 flex-shrink-0 rounded-full"
        onClick={onConfirm}
        disabled={isTranscribing}
        aria-label={t("chat.dictateStop")}
        title={t("chat.dictateStop")}
      >
        <Square className="h-3.5 w-3.5 fill-current" />
      </Button>

      <Button
        type="button"
        variant="darkPrimary"
        size="icon"
        className="h-9 w-9 flex-shrink-0 rounded-full shadow-md"
        onClick={onConfirmAndSend}
        disabled={isTranscribing || !canSend}
        aria-label={
          isTranscribing ? t("chat.dictateTranscribing") : t("chat.dictateSend")
        }
        title={
          isTranscribing ? t("chat.dictateTranscribing") : t("chat.dictateSend")
        }
      >
        {isTranscribing ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Send className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
};

export default DictationRecorder;
